import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useState } from "react";

/* ================= SECTION ================= */

const Section = styled.section`
  padding: 160px 20px;
  background: radial-gradient(
      820px at 18% 0%,
      rgba(245, 158, 11, 0.08),
      transparent 55%
    ),
    radial-gradient(700px at 82% 20%, rgba(56, 189, 248, 0.1), transparent 60%),
    ${({ theme }) => theme.colors.bg};
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 900px;
  margin: auto;
`;

/* ================= HEADER ================= */

const Heading = styled.h2`
  text-align: center;
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled.p`
  text-align: center;
  margin-top: 18px;
  font-size: 17px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= FAQ ITEM ================= */

const Item = styled.div`
  margin-top: 28px;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const Question = styled.button`
  width: 100%;
  padding: 26px 28px;
  background: transparent;
  border: none;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  text-align: left;
`;

const IconWrap = styled.div`
  display: grid;
  place-items: center;
  color: #38bdf8;
`;

const AnswerWrap = styled(motion.div)`
  padding: 0 28px 26px;
`;

const Answer = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= DATA ================= */

const FAQS = [
  {
    q: "Who is skilll.dev for?",
    a: "skilll.dev is for students, freshers, self-taught learners, and career switchers who need clarity on what to learn and how to stay consistent.",
  },
  {
    q: "How is this different from YouTube or courses?",
    a: "YouTube gives content and courses give information. skilll.dev turns that into a structured path with sequencing, daily action, progress tracking, and mentor-style guidance.",
  },
  {
    q: "Will it help me choose between DSA, development, or AI?",
    a: "Yes. The product is meant to reduce that exact confusion by helping users choose a direction based on level, time, and career goal instead of internet noise.",
  },
  {
    q: "Will this help with job readiness?",
    a: "Yes, but through real preparation. The goal is stronger fundamentals, better projects, more consistency, and clearer proof of work, not shortcuts.",
  },
  {
    q: "What is free and what is paid?",
    a: "The roadmap experience can start free, while advanced consistency and mentor-oriented features can live in paid plans depending on the product tier.",
  },
  {
    q: "What if I still feel lost after joining?",
    a: "That is exactly the problem the product is built to solve. The roadmap breaks the journey into smaller steps so users always know the next action.",
  },
];

/* ================= COMPONENT ================= */

export default function FAQSection() {
  const [open, setOpen] = useState(null);

  return (
    <Section>
      <Container>
        <Heading>Frequently Asked Questions</Heading>
        <Sub>Clear answers before you take the next step</Sub>

        {FAQS.map((item, i) => {
          const isOpen = open === i;

          return (
            <Item key={i}>
              <Question onClick={() => setOpen(isOpen ? null : i)}>
                {item.q}
                <IconWrap>
                  {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                </IconWrap>
              </Question>

              <AnimatePresence>
                {isOpen && (
                  <AnswerWrap
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Answer>{item.a}</Answer>
                  </AnswerWrap>
                )}
              </AnimatePresence>
            </Item>
          );
        })}
      </Container>
    </Section>
  );
}
